/**
 * The suite header's "where this runs" bar: the suite's attached clients and
 * (optionally) its server group, plus the sandbox image when computers are on.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUpdateTestSuiteMutation } from "@/data/evals";
import { Globe, Server } from "lucide-react";
import { ClientsPill } from "./clients-pill";
import { SandboxImagePill } from "./sandbox-image-pill";
import { ServerGroupPicker } from "@/components/hosts/ServerGroupPicker";
import { useComputersEnabled } from "@/hooks/useComputersEnabled";
import { convexErrMessage } from "@/lib/convex-error";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import type { HostAttachmentDraft } from "./client-attachments-editor";
import type { EvalSuite } from "./types";

/** Upper bound on clients attached to one suite (one run per client). */
const MAX_SUITE_CLIENTS = 10;

export interface SuiteEnvironmentComposerBarProps {
  suite: EvalSuite;
  readOnly?: boolean;
  /** Client write. Absent ⇒ the whole bar is read-only. */
  onUpdate?: (attachments: HostAttachmentDraft[]) => Promise<void>;
  /** Server-group write. */
  onUpdateServerAttachment?: (serverAttachmentId: string) => Promise<void>;
  className?: string;
  /** `panel` = card surface. `inline` = no chrome, for the header row. */
  containerVariant?: "panel" | "inline";
}

export function SuiteEnvironmentComposerBar({
  suite,
  readOnly = false,
  onUpdate,
  onUpdateServerAttachment,
  className,
  containerVariant = "panel",
}: SuiteEnvironmentComposerBarProps) {
  const editable = !readOnly && Boolean(onUpdate);

  return (
    <BarShell className={className} containerVariant={containerVariant}>
      <SuiteTargetBar
        suite={suite}
        editable={editable}
        onUpdate={onUpdate}
        onUpdateServerAttachment={onUpdateServerAttachment}
      />
    </BarShell>
  );
}

function BarShell({
  children,
  className,
  containerVariant,
}: {
  children: React.ReactNode;
  className?: string;
  containerVariant: "panel" | "inline";
}) {
  return (
    <div
      className={cn(
        containerVariant === "panel"
          ? "rounded-lg bg-card py-2.5 text-card-foreground"
          : "bg-transparent py-0 text-card-foreground",
        className
      )}
      data-testid="suite-environment-bar"
    >
      <div
        className={cn(
          "flex min-h-9 items-center gap-x-2 px-1 sm:px-2",
          containerVariant === "inline"
            ? "w-max min-w-0 max-w-full flex-nowrap"
            : "flex-wrap gap-y-2"
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** The two axes a suite runs: its clients and (optionally) a server group. */
function SuiteTargetBar({
  suite,
  editable,
  onUpdate,
  onUpdateServerAttachment,
}: {
  suite: EvalSuite;
  editable: boolean;
  onUpdate?: (attachments: HostAttachmentDraft[]) => Promise<void>;
  onUpdateServerAttachment?: (serverAttachmentId: string) => Promise<void>;
}) {
  const initialAttachments = useMemo<HostAttachmentDraft[]>(
    () =>
      (suite.hostAttachments ?? []).map((attachment) => ({
        namedHostId: attachment.namedHostId,
        enabledOptionalServerIds: attachment.enabledOptionalServerIds,
      })),
    [suite.hostAttachments]
  );
  const [attachments, setAttachments] =
    useState<HostAttachmentDraft[]>(initialAttachments);
  useEffect(() => {
    setAttachments(initialAttachments);
  }, [initialAttachments]);
  const updateSuite = useUpdateTestSuiteMutation() as unknown as (args: Record<string, unknown>) => Promise<unknown>;

  const persistVersion = useRef(0);
  const persist = useCallback(
    async (next: HostAttachmentDraft[]) => {
      if (!onUpdate) return;
      const previous = attachments;
      const mine = ++persistVersion.current;
      setAttachments(next);
      try {
        await onUpdate(next);
      } catch (error) {
        if (persistVersion.current === mine) setAttachments(previous);
        console.error("Failed to update suite host attachments", error);
      }
    },
    [attachments, onUpdate]
  );

  /**
   * The sandbox image belongs on this bar even in legacy mode — a legacy suite
   * has a real field for it, and it was previously reachable only by opening
   * the settings sheet. Skills are the one strip slot with nowhere to write
   * here (there is no suite-level skills axis), so they stay hidden until the
   * suite is in environment mode.
   */
  const computersEnabled = useComputersEnabled();
  const handleSandboxImageChange = useCallback(
    async (next: string | null) => {
      try {
        await updateSuite({
          suiteId: suite._id,
          // Whole-object replace: `servers`/`serverBindings` are re-sent so
          // setting an image doesn't wipe them, and clearing works by omitting
          // the key rather than sending null.
          environment: {
            servers: suite.environment?.servers ?? [],
            serverBindings: suite.environment?.serverBindings,
            ...(next ? { computerEnvironmentId: next } : {}),
          },
        });
        toast.success(next ? "Computer image set" : "Computer image cleared");
      } catch (error) {
        toast.error(convexErrMessage(error, "Failed to update the suite"));
      }
    },
    [suite._id, suite.environment?.serverBindings, suite.environment?.servers, updateSuite]
  );

  const hostIds = attachments.map((a) => a.namedHostId);

  const handleClientsChange = (nextHostIds: string[]) => {
    // A suite with no clients cannot run; refuse the last detach rather than
    // letting the bar fall into an unrunnable state.
    if (nextHostIds.length === 0) return;
    const byId = new Map(attachments.map((a) => [a.namedHostId, a]));
    void persist(
      nextHostIds.map(
        (namedHostId) =>
          byId.get(namedHostId) ?? {
            namedHostId,
            enabledOptionalServerIds: [],
          }
      )
    );
  };

  const showServers = Boolean(
    suite.projectId && (editable || suite.serverAttachment)
  );

  return (
    <>
      {showServers ? (
        <div className="shrink-0">
          {editable && suite.projectId && onUpdateServerAttachment ? (
            <ServerGroupPicker
              projectId={suite.projectId}
              value={suite.serverAttachmentId ?? null}
              onChange={onUpdateServerAttachment}
            />
          ) : suite.serverAttachment ? (
            <span className="flex h-8 items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 text-xs font-medium text-foreground">
              <Globe className="size-3.5 shrink-0 text-muted-foreground" />
              {suite.serverAttachment.name}
              <span className="text-[10px] text-muted-foreground">
                · {suite.serverAttachment.serverIds.length} server
                {suite.serverAttachment.serverIds.length === 1 ? "" : "s"}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {editable && suite.projectId ? (
          <ClientsPill
            projectId={suite.projectId}
            value={hostIds}
            onChange={handleClientsChange}
            max={MAX_SUITE_CLIENTS}
            testId="suite-env-clients-picker"
          />
        ) : attachments.length === 0 ? (
          <span className="shrink-0 text-[13px] font-normal text-muted-foreground">
            No clients attached
          </span>
        ) : (
          attachments.map((attachment) => {
            const label =
              (suite.hostAttachments ?? []).find(
                (a) => a.namedHostId === attachment.namedHostId
              )?.hostName ?? attachment.namedHostId;
            return (
              <div
                key={attachment.namedHostId}
                className="flex h-8 max-w-[260px] shrink-0 items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 text-foreground"
              >
                <Server className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-xs font-medium">
                  {label}
                </span>
              </div>
            );
          })
        )}
      </div>

      {computersEnabled && editable && suite.projectId ? (
        <div className="shrink-0">
          <SandboxImagePill
            projectId={suite.projectId}
            value={suite.environment?.computerEnvironmentId ?? null}
            onChange={(next) => void handleSandboxImageChange(next)}
            testId="suite-env-sandbox-image"
          />
        </div>
      ) : null}
    </>
  );
}
